import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

// ============================================
// TYPES
// ============================================

export interface HeaderTab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: number; // Per mostrare contatori (es. task in ritardo)
}

export interface HeaderFilterOption {
  value: string;
  label: string;
  color?: string; // Per filtri con indicatori colorati (tags, status)
  icon?: React.ReactNode;
  parentId?: string | null; // Per opzioni gerarchiche (interesse parent)
  hasChildren?: boolean; // Se l'opzione ha figli
}

export interface UserFilterSelection {
  userId: string;
  mode: 'assigned_to' | 'sales_rep_id' | 'both';
}

export interface HeaderFilter {
  id: string;
  type: 'select' | 'multi-checkbox' | 'hierarchical-checkbox' | 'date-range' | 'date-period' | 'search' | 'user' | 'tag' | 'custom';
  label: string;
  options?: HeaderFilterOption[];
  value: any; // Per filtro 'user' sarà UserFilterSelection[]
  onChange: (value: any) => void;
  // Per filtro tipo 'select' con un valore sempre presente (es. ordinamento): il chip
  // resta in stile neutro quando value === defaultValue, invece di apparire "attivo"
  // anche a filtri appena puliti. Cliccare "Tutti" nel dropdown resetta a questo valore
  // invece che a undefined.
  defaultValue?: string;
  /**
   * Valore da cui parte la pagina, quando e' diverso da quello che imposta la
   * voce "Tutti". Serve solo a decidere se mostrare "Pulisci filtri": la
   * Tipologia dei lead parte da "Solo Lead" ma la sua voce "Tutti" vuol dire
   * lead piu' clienti, e i due valori non possono stare nello stesso campo.
   */
  valoreIniziale?: string;
  component?: React.ReactNode; // Per filtri custom
  searchable?: boolean; // Per dropdown con ricerca (es. utenti)
  inverted?: boolean; // Per filtri "inverti" come sezioni (checked = espanda)
  placeholder?: string;
  hideSelectAll?: boolean; // Nasconde "Seleziona tutti / Deseleziona tutti"
  // Per filtro tipo 'user'
  users?: { id: string; full_name?: string; email: string; avatar_url?: string; profile_image_url?: string }[];
  // Filtro 'user': mostra la voce "Proprietario non piu' attivo". Solo per le
  // pagine che sanno tradurla (lead, clienti): altrove finirebbe in una query
  // come se fosse un id e PostgREST risponderebbe 400.
  showInactiveOwner?: boolean;
  // Per filtro tipo 'date-range'
  dateFrom?: string;
  dateTo?: string;
  onDateFromChange?: (value: string) => void;
  onDateToChange?: (value: string) => void;
  // Per filtro tipo 'date-period'
  dateField?: 'created_at' | 'signed_at';
  periodType?: 'year' | 'quarter' | 'custom';
  selectedYear?: number;
  selectedQuarter?: 1 | 2 | 3 | 4 | null;
  onDateFieldChange?: (field: 'created_at' | 'signed_at') => void;
  onPeriodTypeChange?: (type: 'year' | 'quarter' | 'custom') => void;
  onYearChange?: (year: number) => void;
  onQuarterChange?: (quarter: 1 | 2 | 3 | 4 | null) => void;
}

export interface HeaderStat {
  id: string;
  label?: string; // Opzionale - se non presente mostra solo value
  value: number | string;
  color?: string; // 'green', 'red', 'orange', etc.
  icon?: React.ReactNode;
  progressBar?: boolean; // Per mostrare come progress bar invece di testo
  progressValue?: number; // Valore progress (0-100)
  onClick?: () => void; // Per stats cliccabili
  tooltip?: string; // Tooltip on hover
}

export interface HeaderAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  hidden?: boolean; // Per nascondere condizionalmente
  // Visibile ma non cliccabile, con il motivo nel tooltip: serve quando
  // l'azione esiste ma la selezione corrente non la permette (es. "Modifica"
  // su un documento gia' movimentato). Nasconderla farebbe sparire il perche'.
  disabled?: boolean;
  title?: string;
  dataTour?: string; // Attributo data-tour sul bottone (per i tour driver.js)
}

export interface StatusBadgeConfig {
  value: string;
  options: { value: string; label: string; color: string; icon?: any }[];
  onChange: (status: string) => Promise<void>;
  disabled?: boolean;
}

export interface UserAvatarsConfig {
  users: { id: string; full_name?: string; email: string; avatar_url?: string; profile_image_url?: string }[];
  allUsers: { id: string; full_name?: string; email: string; avatar_url?: string }[];
  onAssign: (userId: string) => Promise<void>;
  onUnassign: (userId: string) => Promise<void>;
  maxVisible?: number; // Default 3
}

export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

export interface SortOption {
  field: string;
  label: string;
}

export interface ViewMode {
  id: string;
  icon: React.ReactNode;
  label: string;
}

export interface DynamicHeaderConfig {
  // Identificazione
  sectionId: string;

  // Navigazione
  showBackButton?: boolean;
  onBack?: () => void;

  // Titolo
  icon?: React.ReactNode;
  title: string;
  titleExtra?: React.ReactNode;
  subtitle?: string;

  // Status badge (dropdown colorato)
  statusBadge?: StatusBadgeConfig;

  // User avatars (gruppo modificabile)
  userAvatars?: UserAvatarsConfig;

  // Tabs (opzionali)
  tabs?: HeaderTab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;

  // Stats compatte (opzionali, mostrate a destra delle tabs)
  stats?: HeaderStat[];

  // Filtri (opzionali)
  filters?: HeaderFilter[];
  onClearFilters?: () => void;
  filtersCollapsedByDefault?: boolean; // Default true
  filtersVisibleOnMobileOnly?: boolean; // Se true, bottone e riga filtri visibili solo su mobile

  // Ordinamento (opzionale)
  sortOptions?: SortOption[];
  currentSort?: SortConfig;
  onSortChange?: (sort: SortConfig) => void;

  // View modes (opzionale)
  viewModes?: ViewMode[];
  currentViewMode?: string;
  onViewModeChange?: (mode: string) => void;

  // Azioni (opzionali)
  actions?: HeaderAction[];
  primaryAction?: HeaderAction; // Azione principale (bottone evidenziato)

  // Componenti custom (opzionali)
  customLeft?: React.ReactNode; // Dopo titolo
  /** customLeft prende lo spazio libero e si stringe (es. schede della Dashboard che scorrono di lato). */
  customLeftGrow?: boolean;
  /** Titolo non mostrato nella barra (resta per l'aiuto AI e il contesto della pagina). */
  titleHidden?: boolean;
  customCenter?: React.ReactNode; // Tra tabs e stats
  customRight?: React.ReactNode; // Prima delle azioni (es. DatePicker)
  customFiltersRow?: React.ReactNode; // Aggiunto alla riga filtri

  // Chiave per forzare aggiornamento quando customLeft cambia
  customLeftKey?: string;
  // Chiave per forzare aggiornamento quando customRight cambia
  customRightKey?: string;
  // Chiave per forzare aggiornamento quando customFiltersRow cambia (es. dropdown aperti)
  customFiltersRowKey?: string;
}

// ============================================
// CONTEXT
// ============================================

interface DynamicHeaderContextType {
  config: DynamicHeaderConfig | null;
  setConfig: (config: DynamicHeaderConfig | null) => void;
  updateConfig: (partial: Partial<DynamicHeaderConfig>) => void;
  // Rimuove dallo stack la config con quel sectionId. Usato dai cleanup
  // di usePageHeader quando un drawer/page si smonta, così l'header
  // ritorna automaticamente alla config sotto nello stack (es. drawer
  // Deal sopra drawer Lead → chiudi Deal → torna header del Lead).
  removeConfig: (sectionId: string) => void;
  isHeaderPresent: boolean;
  // Per coordinare l'animazione tra DynamicHeader e FixedHeader
  isFixedHeaderHovered: boolean;
  setIsFixedHeaderHovered: (hovered: boolean) => void;
  // Stato filtri aperti (per coordinare animazione con Layout)
  isFiltersOpen: boolean;
  setIsFiltersOpen: (open: boolean) => void;
  // Forza collasso temporaneo dei filtri (es. quando si apre il drawer dettaglio)
  forceCollapseFilters: boolean;
  setForceCollapseFilters: (v: boolean) => void;
}

const DynamicHeaderContext = createContext<DynamicHeaderContextType | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

interface DynamicHeaderProviderProps {
  children: ReactNode;
}

export function DynamicHeaderProvider({ children }: DynamicHeaderProviderProps) {
  // Stack di config (FIFO): l'ultimo elemento è la config attiva.
  // Stack permette ai drawer sovrapposti (es. drawer Deal sopra drawer
  // Lead) di non perdere la config sottostante quando il drawer in cima
  // si smonta — basta fare pop e l'header torna al precedente.
  const [configStack, setConfigStack] = useState<DynamicHeaderConfig[]>([]);
  const [isFixedHeaderHovered, setIsFixedHeaderHovered] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  // Mantenuto per backward compat — filtri ora sempre visibili, non usato
  const [forceCollapseFilters] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setForceCollapseFilters = (_v: boolean) => {};

  // Fallback anti-flicker: mantiene l'ULTIMO config valido per evitare che
  // l'header sparisca brevemente durante race condition nello stack
  // (es. cleanup di un drawer mentre la pagina sotto si sta rimontando).
  //
  // IMPORTANTE: il fallback deve riflettere SEMPRE il top corrente dello
  // stack, mai un valore stale di un config già rimosso. Update fatto in
  // useEffect (post-render) per evitare side-effect durante render.
  const lastNonNullConfigRef = useRef<DynamicHeaderConfig | null>(null);
  const location = useLocation();

  const rawConfig = configStack.length > 0 ? configStack[configStack.length - 1] : null;
  const config = rawConfig ?? lastNonNullConfigRef.current;

  // Aggiorna il fallback dopo il commit del render — no side-effect durante render.
  // L'invariante "ref non punta mai a un sectionId rimosso" è mantenuta dentro
  // removeConfig (riallinea il ref nello stesso setConfigStack), così qui
  // bastano aggiorna-se-presente / svuota-se-stack-vuoto.
  useEffect(() => {
    if (rawConfig) {
      lastNonNullConfigRef.current = rawConfig;
    } else if (configStack.length === 0) {
      lastNonNullConfigRef.current = null;
    }
  }, [rawConfig, configStack]);

  // Reset aggiuntivo su cambio pathname (es. user va da /deals a /clients)
  useEffect(() => {
    if (configStack.length === 0) {
      lastNonNullConfigRef.current = null;
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const setConfig = useCallback((newConfig: DynamicHeaderConfig | null) => {
    if (newConfig === null) {
      // Mantenuto per retrocompatibilità: setConfig(null) non viene
      // più chiamato da usePageHeader (usa removeConfig), ma se qualche
      // chiamante esterno lo fa, no-op. Per svuotare uno specifico
      // sectionId usare removeConfig.
      return;
    }
    setConfigStack(prev => {
      const idx = prev.findIndex(c => c.sectionId === newConfig.sectionId);
      if (idx === -1) {
        // Push: nuovo sectionId entra in cima allo stack
        return [...prev, newConfig];
      }
      // Replace: stessa sectionId già nello stack → aggiorna in posto
      // mantenendo la posizione (così non sposta in cima un drawer
      // sottostante che ha solo aggiornato un suo campo)
      if (prev[idx] === newConfig) return prev;
      const next = prev.slice();
      next[idx] = newConfig;
      return next;
    });
  }, []);

  const removeConfig = useCallback((sectionId: string) => {
    setConfigStack(prev => {
      const next = prev.filter(c => c.sectionId !== sectionId);
      // Se il fallback punta al sectionId appena rimosso, riallinealo:
      //  - stack vuoto → null (niente di rilevante da mostrare)
      //  - altrimenti → nuovo top dello stack
      // Senza questo, il fallback resterebbe puntato al config del componente
      // appena smontato (es. "Genera PDF" del Deal) e l'header continuerebbe
      // a mostrarlo anche dopo la chiusura del drawer.
      if (lastNonNullConfigRef.current?.sectionId === sectionId) {
        lastNonNullConfigRef.current = next.length > 0 ? next[next.length - 1] : null;
      }
      return next;
    });
  }, []);

  const updateConfig = useCallback((partial: Partial<DynamicHeaderConfig>) => {
    setConfigStack(prev => {
      if (prev.length === 0) return prev;
      const next = prev.slice();
      const top = next[next.length - 1];
      next[next.length - 1] = { ...top, ...partial };
      return next;
    });
  }, []);

  const value: DynamicHeaderContextType = useMemo(() => ({
    config,
    setConfig,
    updateConfig,
    removeConfig,
    isHeaderPresent: config !== null,
    isFixedHeaderHovered,
    setIsFixedHeaderHovered,
    isFiltersOpen,
    setIsFiltersOpen,
    forceCollapseFilters,
    setForceCollapseFilters,
  }), [config, setConfig, updateConfig, removeConfig, isFixedHeaderHovered, isFiltersOpen]);

  return (
    <DynamicHeaderContext.Provider value={value}>
      {children}
    </DynamicHeaderContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

export function useDynamicHeader() {
  const context = useContext(DynamicHeaderContext);
  if (context === undefined) {
    throw new Error('useDynamicHeader must be used within a DynamicHeaderProvider');
  }
  return context;
}

/**
 * Safe version of useDynamicHeader that returns null if no provider is present.
 * Useful for components that may be rendered both inside and outside of DynamicHeaderProvider.
 */
export function useDynamicHeaderSafe() {
  const context = useContext(DynamicHeaderContext);
  return context ?? null;
}
